import { type BlockBlot, type EmbedBlot, type InlineBlot } from 'parchment';
import Quill from 'quill';
import { type BlotConstructorWithNodeType } from '../../extendedQuillTypes';

const Inline = Quill.import('blots/inline') as BlotConstructorWithNodeType<
	HTMLSpanElement,
	InlineBlot
>;
const Block = Quill.import('blots/block') as BlotConstructorWithNodeType<HTMLDivElement, BlockBlot>;
const Break = Quill.import('blots/break') as BlotConstructorWithNodeType<HTMLBRElement, EmbedBlot>;
const Embed = Quill.import('blots/embed') as BlotConstructorWithNodeType<null, EmbedBlot>;

const Link = Quill.import('formats/link') as BlotConstructorWithNodeType<
	HTMLAnchorElement,
	InlineBlot
>;

// Register Rollover format
class RolloverText extends Inline {
	static create(value) {
		const node = super.create();

		node.setAttribute('class', 'showtip indexable');

		value.definition && node.setAttribute('title', value.definition);
		value.capitalizeFirstWord &&
			node.setAttribute('data-glossary-definition-uppercase', value.capitalizeFirstWord);
		value.termInGlossary && node.setAttribute('data-glossary-term-display', value.termInGlossary);
		value.alphaTermInGlossary &&
			node.setAttribute('data-glossary-term-alpha', value.alphaTermInGlossary);

		return node;
	}

	static formats(node) {
		return {
			definition: node.getAttribute('title'),
			capitalizeFirstWord: node.getAttribute('data-glossary-definition-uppercase'),
			termInGlossary: node.getAttribute('data-glossary-term-display'),
			alphaTermInGlossary: node.getAttribute('data-glossary-term-alpha')
		};
	}
}

RolloverText.blotName = 'rolloverText';
RolloverText.tagName = 'a';
RolloverText.className = 'showtip';

// Extend Block format with class attribute
const BLOCK_ATTRIBUTES = ['class'];
class BlockWithAttributes extends Block {
	static create(value) {
		const node = super.create();
		Object.keys(value).map((key) => node.setAttribute(key, value[key]));
		return node;
	}

	static formats(domNode) {
		return BLOCK_ATTRIBUTES.reduce(function (formats, attribute) {
			if (domNode.hasAttribute(attribute)) {
				formats[attribute] = domNode.getAttribute(attribute);
			}
			return formats;
		}, {});
	}

	format(name, value) {
		if (BLOCK_ATTRIBUTES.indexOf(name) > -1) {
			if (value) {
				this.domNode.setAttribute(name, value);
			} else {
				this.domNode.removeAttribute(name);
			}
		} else {
			super.format(name, value);
		}
	}
}
BlockWithAttributes.blotName = 'blockWithAttributes';
BlockWithAttributes.tagName = 'P';

// Extend Div format with class attribute
const DIV_ATTRIBUTES = ['class'];
class DivWithAttributes extends Block {
	static create(value) {
		const node = super.create();
		Object.keys(value).map((key) => node.setAttribute(key, value[key]));

		return node;
	}

	static formats(domNode) {
		return DIV_ATTRIBUTES.reduce(function (formats, attribute) {
			if (domNode.hasAttribute(attribute)) {
				formats[attribute] = domNode.getAttribute(attribute);
			}
			return formats;
		}, {});
	}

	format(name, value) {
		if (DIV_ATTRIBUTES.indexOf(name) > -1) {
			if (value) {
				this.domNode.setAttribute(name, value);
			} else {
				this.domNode.removeAttribute(name);
			}
		} else {
			super.format(name, value);
		}
	}
}

DivWithAttributes.blotName = 'divWithAttributes';
DivWithAttributes.tagName = 'DIV';

// Register Ref format
const REF_ATTRIBUTES = [
	'data-reference-id',
	'data-reference-type',
	'data-source-ids',
	'data-reference-override-presentation',
	'data-citation-prefix',
	'data-citation-suffix',
	'data-reference-page-only'
];
class RefWithAttributes extends Inline {
	static create(value) {
		const node = super.create(value);
		Object.keys(value).map((key) => node.setAttribute(key, value[key]));
		return node;
	}

	static formats(domNode) {
		return REF_ATTRIBUTES.reduce(function (formats, attribute) {
			if (domNode.hasAttribute(attribute)) {
				formats[attribute] = domNode.getAttribute(attribute);
			}
			return formats;
		}, {});
	}

	format(name, value) {
		if (REF_ATTRIBUTES.indexOf(name) > -1) {
			if (value) {
				this.domNode.setAttribute(name, value);
			} else {
				this.domNode.removeAttribute(name);
			}
		} else {
			super.format(name, value);
		}
	}
}
RefWithAttributes.blotName = 'refWithAttributes';
RefWithAttributes.tagName = 'ref';

const LINK_ATTRIBUTES = [
	'class',
	'lms-course-redirect',
	'href',
	'rel',
	'target',
	'name',
	'title',
	'end',
	'start',
	'waveform-player-ref',
	'aria-label',
	'data-page-family-id'
];
class CustomLink extends Link {
	static create(value) {
		const node = document.createElement('a');

		if (typeof value === 'string') {
			// @ts-expect-error `sanitize` exists but Link type isn't exported; see https://github.com/slab/quill/blob/539cbffd0a13b18e9c65eb84dd35e6596e403158/packages/quill/src/formats/link.ts#L21
			node.setAttribute('href', this.sanitize(value));
			node.setAttribute('rel', 'noopener noreferrer');
			node.setAttribute('target', '_blank');
		} else if (typeof value === 'object') {
			Object.keys(value).map((key) => node.setAttribute(key, value[key]));
		}

		return node;
	}

	static formats(domNode) {
		const attributes = domNode.attributes;
		const attrMap = {};

		for (let index = 0; index < attributes.length; index++) {
			const attribute = attributes[index];
			if (LINK_ATTRIBUTES.includes(attribute.name)) attrMap[attribute.name] = attribute.value;
		}

		return attrMap;
	}

	format(name, value) {
		if (LINK_ATTRIBUTES.indexOf(name) > -1) {
			if (value) {
				this.domNode.setAttribute(name, value);
			} else {
				this.domNode.removeAttribute(name);
			}
		} else {
			super.format(name, value);
		}
	}
}

class CustomBreak extends Break {
	length() {
		return 1;
	}
	value() {
		return '\n';
	}

	insertInto(parent, ref) {
		Embed.prototype.insertInto.call(this, parent, ref);
	}
}
CustomBreak.blotName = 'break';
CustomBreak.tagName = 'BR';

class Cite extends Inline {}
Cite.blotName = 'cite';
Cite.tagName = 'cite';

// To enable <span> inside <ref>
// @ts-expect-error `order` exists but not typed; see https://github.com/slab/quill/blob/539cbffd0a13b18e9c65eb84dd35e6596e403158/packages/quill/src/blots/inline.ts#L9
Inline.order = [
	'cursor',
	'inline', // Must be lower
	'underline',
	'strike',
	'italic',
	'bold',
	'link', // Chrome wants <a> to be lower
	'script',
	'footnote-ref-tail',
	'webtext-figure-reference',
	'webtext-source-url',
	'webtext-math',
	'visually-hidden',
	'webtext-glossary-term-locations',
	'webtext-big-link-title',
	'webtext-big-link-credit',
	'webtext-pullquote-heading',
	'key-point',
	'webtext-article-source-name',
	'icon-tech-tip',
	'webtext-decoration',
	'code', // Must be higher,
	'refWithAttributes',
	'cite'
];

export {
	RolloverText,
	RefWithAttributes,
	BlockWithAttributes,
	DivWithAttributes,
	CustomLink,
	CustomBreak,
	Cite
};
